import { useEffect } from "react";
import { AnimatePresence, useReducedMotion } from "motion/react";
import * as m from "motion/react-m";
import { tokens } from "../../styles/Theme/Theme";

const { duration, ease } = tokens.motion;

// The route change: the old page fades out, the scroll resets while nothing is
// visible, and the new page fades in at the top.
//
// It replaces a jump that used to show everything the fix was meant to hide.
// ScrollToTop reset the scroll in an effect - after the new page had already
// painted at the old position - and Bootstrap's `scroll-behavior: smooth` then
// turned that reset into a visible glide down through the new page.
//
// Opacity only, deliberately:
// - no slide, because a transform on this wrapper would become the containing
//   block for every `position: fixed` element inside a page (LiveAppModal).
// - not a true overlap cross-fade, because the outgoing page sits at a deep
//   scroll offset while the incoming one must start at the top; overlapping
//   them means pinning the old page. The fixed background and nav persist
//   through the swap, so fade-out-then-in still reads as a cross-fade.
// - not the View Transitions API: React Router's `viewTransition` needs a data
//   router, and this app uses <BrowserRouter>.
//
// Keyed on pathname, so a query-only change (Work's ?track=) or a same-page
// #hash does not transition. `initial={false}`: the first page load is never
// faded, so above-the-fold content is visible as soon as it renders.

// Runs once per page, after it mounts.
const PageFrame = ({ hash, children }) => {
  useEffect(() => {
    if (!hash) return undefined;
    // A cross-page link to a section (/ux-writing#writing-lab): land on it
    // directly, not with a smooth scroll through everything above it.
    const frame = requestAnimationFrame(() => {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({
        behavior: "instant",
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash]);
  return children;
};

const PageTransition = ({ location, children }) => {
  const reduceMotion = useReducedMotion();
  const seconds = (ms) => (reduceMotion ? 0 : ms / 1000);

  return (
    <AnimatePresence
      mode="wait"
      initial={false}
      onExitComplete={() => window.scrollTo({ top: 0, left: 0, behavior: "instant" })}
    >
      <m.div
        key={location.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: seconds(duration.page), ease: ease.out } }}
        exit={{ opacity: 0, transition: { duration: seconds(duration.exit), ease: ease.in } }}
        onAnimationComplete={(definition) => {
          // The page under the nav changed without a scroll; let it re-check
          // whether it sits over a light or a dark section.
          if (definition?.opacity === 1) window.dispatchEvent(new Event("page:entered"));
        }}
      >
        <PageFrame hash={location.hash}>{children}</PageFrame>
      </m.div>
    </AnimatePresence>
  );
};

export default PageTransition;
